/**
 * Sofia Connect indicative pricing engine — server-safe.
 *
 * Extracted verbatim from src/components/UnifiedConfigurator.tsx.
 * The configurator UI imports the same tables and the same calcPrice()
 * from here. So does the AI advisor prompt builder. Do NOT add
 * currency formatting, i18n, or any React/DOM dependency to this file.
 * A single change here changes what the site quotes and what the
 * assistant says — those must stay in lockstep.
 *
 * All figures are indicative wholesale prices in EUR/month unless the
 * output's `unit` says otherwise. Actual quotes depend on route, term
 * and commercial terms.
 */

/* ═════════════════════════════════════════════════════
   Types
   ═════════════════════════════════════════════════════ */
export type ProductId = "ip-transit" | "l2vpn" | "dwdm" | "dark-fiber";
export type PopRegion = "europe" | "caucasus" | "middle-east" | "bulgaria-domestic";
export type CommitmentMonths = 12 | 24 | 36;
export type BurstModel = "flat" | "95th";
export type RedundancyOption = "single" | "protected" | "fully-redundant";
export type DarkFiberModel = "monthly" | "iru";

export interface PricingPopNode {
  id: string;
  region: PopRegion;
}

/* ═════════════════════════════════════════════════════
   PoP → region map (pricing-only slice of POP_NODES).
   Kept in sync with src/components/UnifiedConfigurator.tsx's
   POP_NODES; the configurator carries the full metadata
   (city, country, tier) but for pricing all that matters is
   the region — that's what drives the DWDM regional table and
   the L2VPN cross-region 1.1× multiplier.
   ═════════════════════════════════════════════════════ */
export const PRICING_POP_NODES: readonly PricingPopNode[] = [
  { id: "sofia", region: "europe" },
  { id: "frankfurt", region: "europe" },
  { id: "istanbul", region: "europe" },
  { id: "athens", region: "europe" },
  { id: "london", region: "europe" },
  { id: "tbilisi", region: "caucasus" },
  { id: "dubai", region: "middle-east" },
  { id: "yerevan", region: "caucasus" },
  { id: "agarak", region: "caucasus" },
  { id: "plovdiv", region: "bulgaria-domestic" },
  { id: "stara-zagora", region: "bulgaria-domestic" },
  { id: "burgas", region: "bulgaria-domestic" },
  { id: "varna", region: "bulgaria-domestic" },
  { id: "balchik", region: "bulgaria-domestic" },
  { id: "dobrich", region: "bulgaria-domestic" },
  { id: "montana", region: "bulgaria-domestic" },
  { id: "pleven", region: "bulgaria-domestic" },
];

/* ═════════════════════════════════════════════════════
   Bandwidth steps
   ═════════════════════════════════════════════════════ */
export interface BwStep {
  value: number;
  label: string;
}

export const BW_IP_TRANSIT: readonly BwStep[] = [
  { value: 1, label: "1G" }, { value: 2, label: "2G" }, { value: 5, label: "5G" },
  { value: 10, label: "10G" }, { value: 25, label: "25G" }, { value: 40, label: "40G" },
  { value: 100, label: "100G" }, { value: 200, label: "200G" }, { value: 400, label: "400G" },
];
/** IP Transit port range for public copy, from the configurator steps. */
export const IP_TRANSIT_MIN_LABEL = BW_IP_TRANSIT[0].label;
export const IP_TRANSIT_MAX_LABEL = BW_IP_TRANSIT[BW_IP_TRANSIT.length - 1].label;
export const IP_TRANSIT_CAPACITY_RANGE_LABEL = `${IP_TRANSIT_MIN_LABEL}\u2013${IP_TRANSIT_MAX_LABEL}`;

export const BW_ETHERNET: readonly BwStep[] = [
  { value: 0.1, label: "100M" }, { value: 0.5, label: "500M" }, { value: 1, label: "1G" },
  { value: 2, label: "2G" }, { value: 5, label: "5G" }, { value: 10, label: "10G" },
];
export const BW_DWDM: readonly BwStep[] = [
  { value: 1, label: "1G" },
  { value: 10, label: "10G" },
  { value: 100, label: "100G" },
  { value: 400, label: "400G" },
];

/**
 * Prose labels derived from BW_DWDM. Every surface that states managed DWDM
 * capacity (service page, hub, FAQ, configurator, AI advisor) reads these,
 * so the sold range and the advertised range cannot drift apart again.
 *
 * 800G is NOT a managed wavelength: today it is offered only as Optical
 * Spectrum, after measuring the line on the route (owner, 23 Oct 2026).
 * See SPECTRUM_HIGH_RATE in src/data/spectrum.ts.
 */
export const DWDM_CAPACITY_RANGE_LABEL = `${BW_DWDM[0].label}\u2013${BW_DWDM[BW_DWDM.length - 1].label}`;
export const DWDM_CAPACITY_MAX_LABEL = BW_DWDM[BW_DWDM.length - 1].label;
export const DWDM_CAPACITY_STEPS_LABEL = BW_DWDM.map((s) => s.label).join(" / ");
/** Range with an instant list price (configurator). Same as the sold range. */
export const DWDM_PRICED_RANGE_LABEL = DWDM_CAPACITY_RANGE_LABEL;

export function getBwSteps(p: ProductId): readonly BwStep[] {
  if (p === "ip-transit") return BW_IP_TRANSIT;
  if (p === "l2vpn") return BW_ETHERNET;
  if (p === "dwdm") return BW_DWDM;
  return [];
}

/* ═════════════════════════════════════════════════════
   L2VPN — per-PoP price ladder
   ═════════════════════════════════════════════════════ */
export const ETHERNET_PRICES: Record<string, Record<number, number>> = {
  // Europe — aligned with market range (mid-range wholesale)
  sofia:     { 0.1: 280, 0.5: 750,  1: 1400, 2: 2600, 5: 5500,  10: 9500 },
  frankfurt: { 0.1: 220, 0.5: 600,  1: 1100, 2: 2000, 5: 4200,  10: 7200 },
  istanbul:  { 0.1: 350, 0.5: 950,  1: 1800, 2: 3400, 5: 7200,  10: 12500 },
  athens:    { 0.1: 360, 0.5: 980,  1: 1850, 2: 3500, 5: 7500,  10: 13000 },
  london:    { 0.1: 200, 0.5: 550,  1: 1000, 2: 1800, 5: 3800,  10: 6500 },
  // Caucasus — CCS submarine cable premium
  tbilisi:   { 0.1: 650, 0.5: 1800, 1: 3500, 2: 6500, 5: 14000, 10: 24000 },
  yerevan:   { 0.1: 900, 0.5: 2500, 1: 4800, 2: 9000, 5: 19000, 10: 34000 },
  agarak:    { 0.1: 1100, 0.5: 3000, 1: 5800, 2: 11000, 5: 23000, 10: 42000 },
  // Middle East — submarine cable route
  dubai:     { 0.1: 800, 0.5: 2200, 1: 4200, 2: 7800, 5: 17000, 10: 30000 },
  // Bulgarian domestic backbone — Sofia Connect Edge (~35% of Sofia pricing)
  plovdiv:      { 0.1: 100, 0.5: 270, 1: 500, 2: 930,  5: 1950, 10: 3350 },
  "stara-zagora": { 0.1: 100, 0.5: 270, 1: 500, 2: 930, 5: 1950, 10: 3350 },
  burgas:       { 0.1: 110, 0.5: 290, 1: 540, 2: 1000, 5: 2100, 10: 3600 },
  varna:        { 0.1: 110, 0.5: 290, 1: 540, 2: 1000, 5: 2100, 10: 3600 },
  balchik:      { 0.1: 130, 0.5: 350, 1: 650, 2: 1200, 5: 2550, 10: 4400 },
  dobrich:      { 0.1: 110, 0.5: 290, 1: 540, 2: 1000, 5: 2100, 10: 3600 },
  montana:      { 0.1: 100, 0.5: 270, 1: 500, 2: 930,  5: 1950, 10: 3350 },
  pleven:       { 0.1: 100, 0.5: 270, 1: 500, 2: 930,  5: 1950, 10: 3350 },
};

/** Route-based L2VPN price: average of origin + destination pricing, +10 % for cross-region. */
export function getRoutePriceL2VPN(origin: string, dest: string, bw: number): number {
  const oPrice = ETHERNET_PRICES[origin]?.[bw] ?? 0;
  const dPrice = ETHERNET_PRICES[dest]?.[bw] ?? 0;
  const oRegion = PRICING_POP_NODES.find((n) => n.id === origin)?.region;
  const dRegion = PRICING_POP_NODES.find((n) => n.id === dest)?.region;
  const crossRegion = oRegion !== dRegion ? 1.1 : 1.0;
  return Math.round(((oPrice + dPrice) / 2) * crossRegion);
}

/* ═════════════════════════════════════════════════════
   IP Transit — capacity → EUR/mo
   ═════════════════════════════════════════════════════ */
export const IP_TRANSIT_PRICES: Record<number, number> = {
  1: 120, 2: 220, 5: 500, 10: 950, 25: 2100, 40: 3200, 100: 7500, 200: 13500, 400: 24000,
};

/* ═════════════════════════════════════════════════════
   DWDM Wavelengths — regional table + route overrides
   ═════════════════════════════════════════════════════ */
export const DWDM_PRICES: Record<PopRegion, Record<number, number>> = {
  europe: { 1: 120, 10: 450, 100: 1500, 400: 4500 },
  caucasus: { 1: 800, 10: 3200, 100: 14000, 400: 48000 },
  "middle-east": { 1: 1400, 10: 5500, 100: 24000, 400: 82000 },
  "bulgaria-domestic": { 1: 150, 10: 520, 100: 1800, 400: 5200 },
};

/** Route-specific DWDM overrides applied before regional lookup.
 *  Keys are `${a}|${b}` with alphabetically sorted POP ids, so
 *  direction (Sofia→Istanbul vs Istanbul→Sofia) is symmetric. */
export const DWDM_ROUTE_OVERRIDES: Record<string, Record<number, number>> = {
  "istanbul|sofia": { 1: 700, 10: 1100, 100: 5500, 400: 13500 },
};

export function getDwdmRouteOverride(origin: string, destination: string, bw: number): number | null {
  const key = [origin, destination].sort().join("|");
  return DWDM_ROUTE_OVERRIDES[key]?.[bw] ?? null;
}

/* ═════════════════════════════════════════════════════
   Dark Fiber — flat per-metre
   ═════════════════════════════════════════════════════ */
export const DARK_FIBER_MONTHLY_PER_METER = 0.03;   // €/metre/month
export const DARK_FIBER_IRU_15YR_PER_METER = 1.0;   // €/metre one-off, 15-year IRU
/** IRU term quoted in public copy; the only IRU term with a price above. */
export const DARK_FIBER_IRU_TERM_YEARS = 15;

/* ═════════════════════════════════════════════════════
   Multipliers
   ═════════════════════════════════════════════════════ */
export function commitmentMultiplierFor(commitment: CommitmentMonths): number {
  if (commitment === 24) return 0.90;
  if (commitment === 36) return 0.82;
  return 1.0;
}

export function burstMultiplierFor(product: ProductId, burstModel: BurstModel): number {
  return product === "ip-transit" && burstModel === "95th" ? 0.85 : 1.0;
}

export function redundancyMultiplierFor(redundancy: RedundancyOption): number {
  if (redundancy === "protected") return 1.30;
  if (redundancy === "fully-redundant") return 1.60;
  return 1.0;
}

/** Enterprise-pricing threshold used by the configurator to swap the
 *  auto-quote CTA for a sales-contact CTA. */
export const ENTERPRISE_THRESHOLD_EUR_MONTHLY = 5000;

/* ═════════════════════════════════════════════════════
   Public inputs / outputs
   ═════════════════════════════════════════════════════ */
export interface PriceInputs {
  product: ProductId;
  origin: string;
  destination: string;
  /** Numeric capacity — meaning depends on product:
   *   ip-transit: 1..400 (Gbps)
   *   l2vpn:      0.1..10 (Gbps)
   *   dwdm:       1..400 (Gbps)
   *   dark-fiber: unused
   */
  bw: number;
  darkFiberModel: DarkFiberModel;
  /** Dark fiber run length in metres. Ignored for other products. */
  darkFiberMeters: number;
  commitment: CommitmentMonths;
  burstModel: BurstModel;
  redundancy: RedundancyOption;
}

export interface PriceResult {
  base: number;
  price: number;
  unit: string;
  isFrom: boolean;
  commitmentMultiplier: number;
  burstMultiplier: number;
  redundancyMultiplier: number;
}

/**
 * Indicative price for a given configurator input. Values, rounding and
 * edge cases match src/components/UnifiedConfigurator.tsx byte-for-byte
 * — this is a pure move, not a rewrite.
 */
export function calcPrice(s: PriceInputs): PriceResult {
  const { product, origin, destination, bw, darkFiberModel, darkFiberMeters, commitment, burstModel, redundancy } = s;

  let base = 0;
  const unit = "/mo";
  let isFrom = false;

  if (product === "ip-transit") {
    base = IP_TRANSIT_PRICES[bw] ?? 0;
  } else if (product === "l2vpn") {
    base = getRoutePriceL2VPN(origin, destination, bw);
  } else if (product === "dwdm") {
    const override = getDwdmRouteOverride(origin, destination, bw);
    if (override !== null) {
      base = override;
    } else {
      const region = PRICING_POP_NODES.find((n) => n.id === origin)?.region ?? "europe";
      base = DWDM_PRICES[region]?.[bw] ?? 0;
    }
    isFrom = true;
  } else {
    // dark-fiber
    if (darkFiberModel === "monthly") {
      const p = Math.round(DARK_FIBER_MONTHLY_PER_METER * darkFiberMeters);
      return { base: p, price: p, unit: "/mo", isFrom: false, commitmentMultiplier: 1, burstMultiplier: 1, redundancyMultiplier: 1 };
    }
    const p = Math.round(DARK_FIBER_IRU_15YR_PER_METER * darkFiberMeters);
    return { base: p, price: p, unit: " IRU (15yr)", isFrom: false, commitmentMultiplier: 1, burstMultiplier: 1, redundancyMultiplier: 1 };
  }

  const commitmentMultiplier = commitmentMultiplierFor(commitment);
  const burstMultiplier = burstMultiplierFor(product, burstModel);
  const redundancyMultiplier = redundancyMultiplierFor(redundancy);

  const price = Math.round(base * commitmentMultiplier * burstMultiplier * redundancyMultiplier);

  return { base, price, unit, isFrom, commitmentMultiplier, burstMultiplier, redundancyMultiplier };
}

/* ═════════════════════════════════════════════════════
   Summary helpers for prompt injection.
   Return short human-readable strings the assistant can quote.
   ═════════════════════════════════════════════════════ */

export interface PricingSummary {
  ipTransitBandsEur: string;
  l2vpnRangeEur: string;
  dwdmRangeEur: string;
  darkFiberEur: string;
  commitmentTerms: string;
  redundancyOptions: string;
  burstOptions: string;
  enterpriseThresholdEur: number;
}

/** Precomputed, prompt-safe summary of the pricing tables. Consumed by
 *  the SC-NIA prompt builder. No numeric literal in the assistant's
 *  prompt is ever hand-typed — everything here is derived. */
export function getPricingSummary(): PricingSummary {
  const ipBands = Object.entries(IP_TRANSIT_PRICES)
    .map(([gbps, eur]) => `${gbps}G≈€${eur.toLocaleString("en-US")}`)
    .join(", ");

  // L2VPN indicative single-PoP range across Europe (cheapest to premium),
  // using the Sofia and Agarak lines as end-points.
  const l2vpnLow100M = ETHERNET_PRICES.london[0.1];      // cheapest EU 100M
  const l2vpnHigh10G = ETHERNET_PRICES.agarak[10];        // premium 10G
  const l2vpnRange = `€${l2vpnLow100M.toLocaleString("en-US")} (100M London) → €${l2vpnHigh10G.toLocaleString("en-US")} (10G Agarak, per-side; route price averages origin+destination and adds 10% cross-region)`;

  const dwdmEu1G = DWDM_PRICES.europe[1];
  const dwdmEu400G = DWDM_PRICES.europe[400];
  const dwdmBg1G = DWDM_PRICES["bulgaria-domestic"][1];
  const dwdmMe400G = DWDM_PRICES["middle-east"][400];
  const dwdmRange = `Europe 1G €${dwdmEu1G.toLocaleString("en-US")} → 400G €${dwdmEu400G.toLocaleString("en-US")}; Bulgaria-domestic 1G €${dwdmBg1G.toLocaleString("en-US")}; Middle-East 400G €${dwdmMe400G.toLocaleString("en-US")} (regional table; route-specific overrides may apply, e.g. Sofia↔Istanbul)`;

  const dfMonthly = DARK_FIBER_MONTHLY_PER_METER;
  const dfIru = DARK_FIBER_IRU_15YR_PER_METER;
  const darkFiber = `Bulgaria only. Monthly ~€${dfMonthly.toFixed(2)}/m/month, or 15-year IRU ~€${dfIru.toFixed(2)}/m one-off`;

  const commit = `12 mo (list), 24 mo (-${Math.round((1 - commitmentMultiplierFor(24)) * 100)}%), 36 mo (-${Math.round((1 - commitmentMultiplierFor(36)) * 100)}%)`;
  const redundancy = `single (list), protected 1+1 (+${Math.round((redundancyMultiplierFor("protected") - 1) * 100)}%), fully-redundant (+${Math.round((redundancyMultiplierFor("fully-redundant") - 1) * 100)}%)`;
  const burst = `IP Transit only — flat (list) or 95th-percentile burst (-${Math.round((1 - burstMultiplierFor("ip-transit", "95th")) * 100)}%)`;

  return {
    ipTransitBandsEur: ipBands,
    l2vpnRangeEur: l2vpnRange,
    dwdmRangeEur: dwdmRange,
    darkFiberEur: darkFiber,
    commitmentTerms: commit,
    redundancyOptions: redundancy,
    burstOptions: burst,
    enterpriseThresholdEur: ENTERPRISE_THRESHOLD_EUR_MONTHLY,
  };
}
